const SW_CU_HINT_KEY = 'sw-cu:last';
const SW_CU_HINT_FIRST_KEY = 'sw-cu:first';
const SW_CU_OFF_KEY = 'sw-cu:off';
const SW_CU_RECONCILED_KEY = 'sw-cu:rc';
const SW_CU_TOKEN = 'sw-upsell';
const SW_CU_GIFT_KEYS = ['_isFree', '_is_free', 'isFree', '_freeSample'];
const SW_CU_OFFER_STATES = ['offer_sub', 'offer_onetime', 'restore'];
const SW_CU_ACTIONS = ['add', 'convert', 'restore', 'readd'];

const swCu = {
  queue: Promise.resolve(),
  pending: new Set(),
  reconciled: new Set(),
  foreignRenders: 0,
  renderMark: 0,
  observer: null,
  watched: null,
  renderedProductId: null,
  errorFor: null,
};

function swCuRead(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function swCuWrite(key, value) {
  try {
    if (value === null || value === undefined) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, String(value));
    }
  } catch (error) {
    return;
  }
}

function swCuHint(id) {
  swCuWrite(SW_CU_HINT_KEY, id);
  swCuWrite(SW_CU_HINT_FIRST_KEY, null);
}

function swCuIsOff() {
  return swCuRead(SW_CU_OFF_KEY) === '1';
}

function swCuDisable() {
  swCuWrite(SW_CU_OFF_KEY, '1');
  document.documentElement.classList.add('sw-cu-off');
}

function swCuUrl(path) {
  return `${window.Shopify?.routes?.root || '/'}${path}`;
}

function swCuSectionIds() {
  const miniCart = document.querySelector('mini-cart');
  return miniCart?.getSectionsToRender ? miniCart.getSectionsToRender().map((section) => section.id) : [];
}

function swCuRenderCount() {
  const target = document.getElementById('mini-cart');
  if (target && target !== swCu.watched && typeof MutationObserver === 'function') {
    if (!swCu.observer) {
      swCu.observer = new MutationObserver(() => {
        swCu.foreignRenders += 1;
      });
    }
    swCu.observer.disconnect();
    swCu.observer.observe(target, { childList: true });
    swCu.watched = target;
  }
  if (swCu.observer?.takeRecords().length) swCu.foreignRenders += 1;
  return swCu.foreignRenders;
}

async function swCuGetCart() {
  const response = await fetch(swCuUrl(`cart.js?t=${Date.now()}`), { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`cart ${response.status}`);
  return response.json();
}

async function swCuPost(path, body, withSections = false) {
  const payload = withSections
    ? { ...body, sections: swCuSectionIds(), sections_url: window.location.pathname }
    : body;
  if (withSections) swCu.renderMark = swCuRenderCount();
  const response = await fetch(swCuUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.description || data.message || `cart ${response.status}`);
    error.status = response.status;
    throw error;
  }
  return data;
}

async function swCuFetchSections() {
  const ids = swCuSectionIds();
  if (!ids.length) return null;
  swCu.renderMark = swCuRenderCount();
  const response = await fetch(swCuUrl(`?sections=${ids.join(',')}&t=${Date.now()}`));
  if (!response.ok) return null;
  return response.json();
}

function swCuTruthy(value) {
  return ['true', '1', 'yes'].includes(String(value ?? '').trim().toLowerCase());
}

function swCuIsGift(item) {
  const properties = item.properties || {};
  return (
    SW_CU_GIFT_KEYS.some((key) => swCuTruthy(properties[key])) ||
    String(item.vendor || '').toLowerCase() === 'onward'
  );
}

function swCuMarker(item) {
  return String(item.properties?._sw_upsell ?? '').trim();
}

function swCuPlanOf(item) {
  return item.selling_plan_allocation?.selling_plan?.id ?? null;
}

function swCuFindAddon(cart, variantId) {
  return cart.items.findIndex(
    (item) => String(item.variant_id) === String(variantId) && swCuMarker(item) !== '' && !swCuIsGift(item)
  );
}

function swCuFindParent(cart, productId, variantId, subscribed) {
  const candidates = cart.items
    .map((item, index) => ({ item, index }))
    .filter(
      ({ item }) =>
        String(item.product_id) === String(productId) &&
        swCuMarker(item) === '' &&
        !swCuIsGift(item) &&
        (swCuPlanOf(item) !== null) === subscribed
    );
  const exact = candidates.find(({ item }) => String(item.variant_id) === String(variantId));
  return (exact || candidates[0])?.index ?? -1;
}

function swCuFindLine(cart, step) {
  return cart.items.findIndex(
    (item) =>
      String(item.variant_id) === String(step.variantId) &&
      (swCuMarker(item) !== '') === step.marked &&
      !swCuIsGift(item) &&
      String(swCuPlanOf(item)) === String(step.planAfter)
  );
}

// Properties are left out on purpose: change.js keeps them, so a mis-hit line can never lose _isFree or the marker.
function swCuSetPlan(cart, index, sellingPlan, withSections = false, quantity = cart.items[index].quantity) {
  return swCuPost('cart/change.js', { line: index + 1, quantity, selling_plan: sellingPlan }, withSections);
}

function swCuStrayStep(before, after, step) {
  const kept = new Set(before.items.map((item) => item.key));
  const hit = after.items.find((item) => !kept.has(item.key) && String(swCuPlanOf(item)) === String(step.planAfter));
  if (!hit) return null;
  const original = before.items.find(
    (item) =>
      !after.items.some((line) => line.key === item.key) &&
      String(item.variant_id) === String(hit.variant_id) &&
      swCuMarker(item) === swCuMarker(hit)
  );
  if (!original) return null;
  return {
    variantId: hit.variant_id,
    marked: swCuMarker(hit) !== '',
    planAfter: step.planAfter,
    planBefore: swCuPlanOf(original),
    quantityBefore: original.quantity,
  };
}

async function swCuMovePlan(task, cart, index, step, withSections) {
  const response = await swCuSetPlan(cart, index, step.planAfter, withSections);
  task.touched = true;
  task.steps.push({ ...step, quantityBefore: cart.items[index].quantity });
  if (swCuFindLine(response, step) === -1) {
    const stray = swCuStrayStep(cart, response, step);
    if (stray) task.steps.push(stray);
    throw new Error('line moved');
  }
  return response;
}

function swCuParentStep(task, parent) {
  return { variantId: parent.variant_id, marked: false, planAfter: Number(task.parentPlanId), planBefore: null };
}

async function swCuVerify(task) {
  const cart = await swCuGetCart();
  const line = cart.items[swCuFindAddon(cart, task.variantId)];
  const verified =
    line &&
    swCuPlanOf(line) !== null &&
    (line.line_level_discount_allocations || []).length > 0 &&
    line.final_price <= Number(task.price) + 1;
  if (!verified) throw new Error('offer not applied');
  return cart;
}

async function swCuAddAndVerify(task) {
  const added = await swCuPost(
    'cart/add.js',
    {
      items: [
        {
          id: Number(task.variantId),
          quantity: 1,
          selling_plan: Number(task.planId),
          properties: {
            _sw_upsell: String(task.parentProductId),
            ...(task.parentHandle ? { _sw_upsell_parent: task.parentHandle } : {}),
          },
        },
      ],
    },
    true
  );
  task.touched = true;
  task.steps.push({ remove: true, variantId: task.variantId });
  const cart = await swCuVerify(task);
  return { sections: added.sections, cart, mutated: true, message: task.messages.added };
}

async function swCuUndo(steps) {
  for (const step of [...steps].reverse()) {
    try {
      const cart = await swCuGetCart();
      if (step.removeKey) {
        if (cart.items.some((item) => item.key === step.removeKey)) {
          await swCuPost('cart/change.js', { id: step.removeKey, quantity: 0 });
        }
      } else if (step.remove) {
        const index = swCuFindAddon(cart, step.variantId);
        if (index !== -1) await swCuPost('cart/change.js', { id: cart.items[index].key, quantity: 0 });
      } else {
        const index = swCuFindLine(cart, step);
        if (index !== -1) await swCuSetPlan(cart, index, step.planBefore, false, step.quantityBefore);
      }
    } catch (error) {
      continue;
    }
  }
}

function swCuGiftQualifies(cart) {
  return Boolean(cart?.items && window.gwpManager?.isBogoEligible?.(cart));
}

async function swCuRepaint(cart) {
  return { sections: await swCuFetchSections(), cart, mutated: false };
}

const swCuActions = {
  async add(task) {
    const cart = await swCuGetCart();
    if (swCuFindAddon(cart, task.variantId) !== -1) return swCuRepaint(cart);
    if (swCuFindParent(cart, task.parentProductId, task.parentVariantId, true) === -1) return swCuRepaint(cart);
    return swCuAddAndVerify(task);
  },

  async convert(task) {
    const cart = await swCuGetCart();
    if (swCuFindAddon(cart, task.variantId) !== -1) return swCuRepaint(cart);
    if (swCuFindParent(cart, task.parentProductId, task.parentVariantId, true) === -1) {
      const index = swCuFindParent(cart, task.parentProductId, task.parentVariantId, false);
      if (index === -1 || !task.parentPlanId) return swCuRepaint(cart);
      task.unlocksGift = !swCuGiftQualifies(cart);
      const step = swCuParentStep(task, cart.items[index]);
      await swCuMovePlan(task, cart, index, step, false);
    }
    return swCuAddAndVerify(task);
  },

  async restore(task) {
    let cart = await swCuGetCart();
    let index = swCuFindAddon(cart, task.variantId);
    if (index === -1) return swCuRepaint(cart);

    const addonOnPlan = swCuPlanOf(cart.items[index]) !== null;
    const parentNeeded =
      Boolean(task.parentPlanId) && swCuFindParent(cart, task.parentProductId, task.parentVariantId, true) === -1;
    if (!parentNeeded && addonOnPlan) return swCuRepaint(cart);

    let response = null;
    if (parentNeeded) {
      const parentIndex = swCuFindParent(cart, task.parentProductId, task.parentVariantId, false);
      if (parentIndex === -1) return swCuRepaint(cart);
      task.unlocksGift = !swCuGiftQualifies(cart);
      const step = swCuParentStep(task, cart.items[parentIndex]);
      response = await swCuMovePlan(task, cart, parentIndex, step, addonOnPlan);
    }

    if (!addonOnPlan) {
      cart = await swCuGetCart();
      index = swCuFindAddon(cart, task.variantId);
      if (index === -1) throw new Error('add-on missing');
      const step = { variantId: cart.items[index].variant_id, marked: true, planAfter: Number(task.planId), planBefore: null };
      response = await swCuMovePlan(task, cart, index, step, true);
    }

    const verified = await swCuVerify(task);
    return { sections: response.sections, cart: verified, mutated: true, message: task.messages.restored };
  },

  async readd(task) {
    let cart = await swCuGetCart();
    let index = swCuFindAddon(cart, task.variantId);
    if (index === -1 || !task.parentVariantId || !task.parentPlanId) return swCuRepaint(cart);

    if (swCuFindParent(cart, task.parentProductId, task.parentVariantId, true) === -1) {
      task.unlocksGift = !swCuGiftQualifies(cart);
      const added = await swCuPost('cart/add.js', {
        items: [{ id: Number(task.parentVariantId), quantity: 1, selling_plan: Number(task.parentPlanId) }],
      });
      task.touched = true;
      const parentKey = added.items?.[0]?.key;
      if (parentKey) task.steps.push({ removeKey: parentKey });
      cart = await swCuGetCart();
      index = swCuFindAddon(cart, task.variantId);
      if (index === -1) throw new Error('add-on missing');
    }

    let response = null;
    if (swCuPlanOf(cart.items[index]) === null) {
      const step = { variantId: cart.items[index].variant_id, marked: true, planAfter: Number(task.planId), planBefore: null };
      response = await swCuMovePlan(task, cart, index, step, true);
    }
    const verified = await swCuVerify(task);
    const sections = response?.sections || (await swCuFetchSections());
    return { sections, cart: verified, mutated: true, message: task.messages.restored };
  },

  async remove(task) {
    let response;
    try {
      response = await swCuPost('cart/change.js', { id: task.key, quantity: 0 }, true);
    } catch (error) {
      if (error.status !== 400) throw error;
      const cart = await swCuGetCart();
      const index = swCuFindAddon(cart, task.variantId);
      if (index === -1) return swCuRepaint(cart);
      response = await swCuPost('cart/change.js', { id: cart.items[index].key, quantity: 0 }, true);
    }
    return { sections: response.sections, cart: response, mutated: true };
  },

  async reconcile(task) {
    const cart = await swCuGetCart();
    let index = cart.items.findIndex((item) => item.key === task.key);
    if (index === -1) {
      index = swCuFindLine(cart, { variantId: task.variantId, marked: true, planAfter: task.planId });
    }
    const line = cart.items[index];
    if (!line || swCuMarker(line) === '' || swCuIsGift(line)) return { cart, mutated: false };
    const target = task.target ? Number(task.target) : null;
    if (target === null) {
      if (swCuPlanOf(line) === null) return { cart, mutated: false };
      if (swCuFindParent(cart, swCuMarker(line), null, true) !== -1) return { cart, mutated: false };
    } else {
      const subscribed = cart.items.some((item) => swCuMarker(item) === '' && !swCuIsGift(item) && swCuPlanOf(item) !== null);
      if (swCuPlanOf(line) !== null || !subscribed) return { cart, mutated: false };
    }

    const response = await swCuSetPlan(cart, index, target, true);
    task.touched = true;
    const step = { variantId: line.variant_id, marked: true, planAfter: target, planBefore: swCuPlanOf(line), quantityBefore: line.quantity };
    if (swCuFindLine(response, step) === -1) {
      const stray = swCuStrayStep(cart, response, step);
      if (stray) task.steps.push(stray);
      throw new Error('line moved');
    }
    if (target !== null) {
      task.steps.push(step);
      await swCuVerify(task);
    }
    return { sections: response.sections, cart: response, mutated: true };
  },
};

async function swCuWaitForGwp(gwp) {
  if (!gwp) return;
  const deadline = Date.now() + 3000;
  while (
    (gwp.isProcessing || gwp.cleanupInFlight || gwp.customerActionInFlight?.size) &&
    Date.now() < deadline
  ) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
}

function swCuRender(sections) {
  const miniCart = document.querySelector('mini-cart');
  if (!miniCart || !sections) return;

  swCuRenderCount();
  // renderContents always opens the drawer, which a background reconcile must not do.
  if (miniCart.closest('details')?.hasAttribute('open') && typeof miniCart.renderContents === 'function') {
    try {
      miniCart.renderContents({ sections });
    } catch (error) {
      swCuRenderSections(miniCart, sections);
    }
  } else {
    swCuRenderSections(miniCart, sections);
  }
  swCu.observer?.takeRecords();
}

function swCuRenderSections(miniCart, sections) {
  miniCart.getSectionsToRender().forEach((section) => {
    const target = document.getElementById(section.id);
    if (!target || !sections[section.id]) return;
    target.innerHTML = miniCart.getSectionInnerHTML(sections[section.id], section.selector);
  });
}

async function swCuRecover(task) {
  const rolledBack = task.steps.length > 0;
  if (rolledBack) {
    await swCuUndo(task.steps);
    swCuDisable();
  }
  const mutated = rolledBack || Boolean(task.touched);
  if (!task.interactive && !mutated) return { mutated: false };
  const sections = await swCuFetchSections().catch(() => null);
  return { sections, mutated, error: task.interactive };
}

async function swCuRun(task) {
  const gwp = window.gwpManager;
  await swCuWaitForGwp(gwp);
  gwp?.customerActionInFlight?.add(SW_CU_TOKEN);
  let result = {};

  try {
    try {
      if (SW_CU_ACTIONS.includes(task.type) && swCuIsOff()) {
        result = { cart: await swCuGetCart(), mutated: false };
      } else {
        result = await swCuActions[task.type](task);
      }
    } catch (error) {
      result = await swCuRecover(task);
    }

    // A full /cart.js or change.js body only: an add.js body is a partial cart and would corrupt GWP's cache.
    if (result.cart?.items && typeof gwp?.updateCartCache === 'function') gwp.updateCartCache(result.cart);
    // A gift refused on an earlier cart would block the one this click just unlocked.
    if (task.unlocksGift && result.mutated && !result.error && swCuGiftQualifies(result.cart)) {
      gwp?.setOptOut?.('bogo', null, false);
    }

    // Another script re-rendered the drawer after these sections were requested, so they may be older than the DOM.
    let sections = result.sections;
    if (sections && swCuRenderCount() !== swCu.renderMark) {
      sections = await swCuFetchSections().catch(() => null);
    }
    if (sections) {
      const details = document.querySelector('cart-drawer details');
      const hadFocus = details?.contains(document.activeElement) && document.activeElement !== details;
      if (result.error) swCu.errorFor = String(task.variantId);
      swCuRender(sections);
      if (!task.interactive && hadFocus && typeof trapFocus === 'function') {
        trapFocus(details, document.getElementById('mini-cart'));
      }
      if (typeof gwp?.commitRender === 'function') gwp.commitRender();
    }

    if (result.mutated) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'sw-cart-upsell' } }));
    }
  } finally {
    gwp?.customerActionInFlight?.delete(SW_CU_TOKEN);
    if (typeof gwp?.drainRecheck === 'function') gwp.drainRecheck();
    swCuRelease(task);
  }

  if (task.interactive) setTimeout(() => swCuSettle(task, result), 100);
}

function swCuEnqueue(task) {
  swCu.pending.add(task.id);
  swCu.queue = swCu.queue
    .then(() => swCuRun(task))
    .catch(() => {})
    .finally(() => swCu.pending.delete(task.id));
}

function swCuRelease(task) {
  if (task.button?.isConnected) task.button.removeAttribute('aria-busy');
  if (task.overlay?.isConnected) task.overlay.classList.add('hidden');
}

function swCuVisible(element) {
  return Boolean(element && element.getClientRects().length);
}

function swCuSettle(task, result) {
  const id = String(task.variantId || '');
  const message = result.error ? task.messages.error : result.message;
  const status = document.querySelector('#mini-cart .sw-cart-upsell__status');
  if (status && message) status.textContent = message;

  if (result.error && task.messages.error && task.type === 'remove') {
    const remove =
      document.querySelector(`#mini-cart [data-sw-cu-remove][data-line-key="${CSS.escape(task.key)}"]`) ||
      document.querySelector(`#mini-cart [data-sw-cu-remove][data-variant-id="${id}"]`);
    const text = remove?.closest('li')?.querySelector('[id^="Line-item-error-"] .cart-item__error-text');
    if (text) text.textContent = task.messages.error;
  } else if (result.error && task.messages.error) {
    const scope = task.origin === 'line' ? '.sw-cart-upsell-line' : '.sw-cart-upsell';
    const slots = Array.from(document.querySelectorAll(`#mini-cart ${scope} [data-sw-cu-error-for="${id}"]`));
    const slot = slots.find((element) => swCuVisible(element.parentElement)) || slots[0];
    if (slot) {
      slot.querySelector('.sw-cart-upsell__error-text').textContent = task.messages.error;
      slot.hidden = false;
    }
  }
  swCu.errorFor = null;

  const details = document.querySelector('cart-drawer details');
  if (!details?.hasAttribute('open')) return;
  const active = document.activeElement;
  if (active && active !== document.body && details.contains(active)) return;

  const target =
    [
      `#mini-cart .sw-cart-upsell__set:not([hidden]) .sw-cart-upsell__cta[data-offer-variant-id="${id}"]`,
      `#mini-cart [data-sw-cu-action][data-offer-variant-id="${id}"]`,
      `#mini-cart [data-sw-cu-remove][data-variant-id="${id}"]`,
    ]
      .map((selector) => document.querySelector(selector))
      .find(swCuVisible) || document.getElementById('mini-cart');
  if (!target) return;

  // The drawer's focus trap caches its first and last elements, and the render replaced both.
  if (typeof trapFocus === 'function') {
    trapFocus(details, target);
  } else {
    target.focus();
  }
}

function swCuMessages(element) {
  const source = element.closest('[data-msg-error]') || document.querySelector('#mini-cart [data-msg-error]');
  const data = source?.dataset || {};
  return { added: data.msgAdded || '', restored: data.msgRestored || '', error: data.msgError || '' };
}

function swCuActionTask(button) {
  const data = button.dataset;
  if (!SW_CU_ACTIONS.includes(data.swCuAction) || !data.offerVariantId) return null;
  const messages = swCuMessages(button);
  messages.added = messages.added.replace(/\[title\]/g, data.offerTitle || '');
  return {
    id: `${data.swCuAction}:${data.offerVariantId}`,
    type: data.swCuAction,
    interactive: true,
    button,
    origin: button.closest('.sw-cart-upsell-line') ? 'line' : 'card',
    variantId: data.offerVariantId,
    planId: data.planId,
    parentProductId: data.parentProductId,
    parentVariantId: data.parentVariantId,
    parentPlanId: data.parentPlanId,
    parentHandle: data.parentHandle,
    price: data.price,
    messages,
    steps: [],
  };
}

function swCuRemoveTask(button) {
  const data = button.dataset;
  if (!data.lineKey) return null;
  const messages = swCuMessages(button);
  messages.error = messages.error || window.theme?.cartStrings?.error || '';
  return {
    id: `remove:${data.variantId}`,
    type: 'remove',
    interactive: true,
    button,
    overlay: button.closest('li')?.querySelector('.loading-overlay') || null,
    key: data.lineKey,
    variantId: data.variantId,
    messages,
    steps: [],
  };
}

function swCuSetMatches(set, id) {
  return set.dataset.parentProductId === id || (set.dataset.parentVariantIds || '').split(' ').includes(id);
}

class SwCartUpsell extends HTMLElement {
  connectedCallback() {
    this.applyOff();
    this.applyHint();
    this.reconcile();
  }

  applyOff() {
    if (!swCuIsOff()) return;
    this.querySelectorAll('.sw-cart-upsell__item').forEach((item) => {
      if (!SW_CU_OFFER_STATES.includes(item.dataset.state)) return;
      if (swCu.errorFor && item.dataset.offerVariantId === swCu.errorFor) {
        item.querySelector('[data-sw-cu-action]')?.setAttribute('aria-disabled', 'true');
        return;
      }
      item.hidden = true;
    });
  }

  applyHint() {
    const sets = Array.from(this.querySelectorAll('.sw-cart-upsell__set'));
    if (!sets.length) return;

    // renderContents stores the added line's variant id on <mini-cart>, which covers add paths that fire no event.
    const rendered = document.querySelector('mini-cart')?.productId;
    if (!rendered) {
      swCu.renderedProductId = null;
    } else if (String(rendered) !== swCu.renderedProductId) {
      swCu.renderedProductId = String(rendered);
      if (sets.some((set) => swCuSetMatches(set, swCu.renderedProductId))) {
        swCuHint(swCu.renderedProductId);
      }
    }

    const live = sets.filter((set) => set.querySelector('.sw-cart-upsell__item:not([hidden])'));
    const first = live[0]?.dataset.parentProductId || '';
    let hint = swCuRead(SW_CU_HINT_KEY);
    const pinned = swCuRead(SW_CU_HINT_FIRST_KEY);
    // A newer line arrived through a path that left no hint, so the newest-first pick wins again.
    if (hint && pinned !== null && pinned !== first) {
      hint = null;
      swCuWrite(SW_CU_HINT_KEY, null);
    }
    if (hint) swCuWrite(SW_CU_HINT_FIRST_KEY, first);
    const active = (hint && live.find((set) => swCuSetMatches(set, hint))) || live[0] || null;
    sets.forEach((set) => {
      set.hidden = set !== active;
    });
    this.hidden = !active && !this.querySelector('.sw-cart-upsell__notices');
  }

  reconcile() {
    if (window.Shopify?.designMode) return;

    let entries = [];
    try {
      entries = JSON.parse(this.dataset.reconcile || '[]');
    } catch (error) {
      return;
    }
    if (!Array.isArray(entries)) return;

    let stored = [];
    try {
      stored = JSON.parse(swCuRead(SW_CU_RECONCILED_KEY) || '[]');
    } catch (error) {
      stored = [];
    }
    if (!Array.isArray(stored)) stored = [];

    // Forget ids that left the list (reconciled or restored) so a later lapse runs again.
    const entryId = (entry) => `${entry.key}|${entry.planId}|${entry.target ?? ''}`;
    const current = new Set(entries.map(entryId));
    stored = stored.filter((id) => current.has(id));
    swCu.reconciled.forEach((id) => {
      if (!current.has(id)) swCu.reconciled.delete(id);
    });

    entries.forEach((entry) => {
      const id = entryId(entry);
      if (!entry.key || swCu.reconciled.has(id) || stored.includes(id)) return;
      swCu.reconciled.add(id);
      stored.push(id);
      swCuEnqueue({
        id: `reconcile:${id}`,
        type: 'reconcile',
        interactive: false,
        key: entry.key,
        variantId: entry.variantId,
        planId: entry.planId,
        target: entry.target ?? null,
        price: entry.price,
        messages: {},
        steps: [],
      });
    });
    swCuWrite(SW_CU_RECONCILED_KEY, JSON.stringify(stored.slice(-50)));
  }
}

if (!customElements.get('sw-cart-upsell')) customElements.define('sw-cart-upsell', SwCartUpsell);

if (swCuIsOff()) document.documentElement.classList.add('sw-cu-off');

document.addEventListener('click', (event) => {
  const origin = event.target instanceof Element ? event.target : null;
  const remove = origin?.closest('[data-sw-cu-remove]');
  const button = remove || origin?.closest('[data-sw-cu-action]');
  if (!button) return;

  event.preventDefault();
  if (button.getAttribute('aria-busy') === 'true' || button.getAttribute('aria-disabled') === 'true') return;

  const task = remove ? swCuRemoveTask(button) : swCuActionTask(button);
  if (!task || swCu.pending.has(task.id)) return;

  if (task.parentProductId) swCuHint(task.parentProductId);
  button.setAttribute('aria-busy', 'true');
  task.overlay?.classList.remove('hidden');
  swCuEnqueue(task);
});

document.addEventListener('ajaxProduct:added', (event) => {
  const product = event.detail?.product;
  const line = Array.isArray(product?.items) ? product.items[0] : product;
  if (line?.product_id) swCuHint(line.product_id);
});

document.addEventListener('cart:updated', (event) => {
  if (event.detail?.source !== 'octane-quiz') return;
  swCuHint(null);
  document.querySelectorAll('sw-cart-upsell').forEach((element) => element.applyHint());
});

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => {
    if (data?.productVariantId) swCuHint(data.productVariantId);
  });
}
